import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Badge, message, notification, Empty, Tooltip, DatePicker, Select } from 'antd';
import moment from 'moment';
import { formatDate, formatDateTime } from '../../../Utils/dateFormat';
import {
  DownloadOutlined,
  PrinterOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LeftOutlined,
  RightOutlined,
  SettingOutlined
} from '@ant-design/icons';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';
import { ThemeProvider } from 'styled-components';
import {
  getJobBoardByStatusAndDate,
  getJobBoardsUser,
  downloadJobBoard
} from '../../../Apis/JobBoard';
import {
  getColumnPreferences,
  saveColumnPreferences
} from '../../../Apis/Clinic';
import { PrintButton, StyledViewJobButton } from '../Audit/styledComponents';
import { SettingsButton } from '../JobList/styledComponents';
import NotesBoardColumnSettingsModal, {
  DEFAULT_COLUMNS
} from './NotesBoardColumnSettingsModal';
import { useSelector } from 'react-redux';
import { ContainerRow, TitleRow } from '../styledComponents';
import BreadCrumbComponent from './../../CustomComponents/BreadCrumbComponent';
import CustomTypography from '../../CustomComponents/CustomTypography';
import styled from 'styled-components';
import { useReactToPrint } from 'react-to-print';
import { StyledJobListTabPaneContentCount } from '../JobList/styledComponents';
import CustomSideDrawer from '../../CustomComponents/CustomSideDrawer';
import SessionDetails from '../Audit/SessionDetails';
import Spinner from '../../CustomComponents/Spinner';
import * as Styled from '../JobList/TanStackTableStyles';
import { createNotesBoardColumns } from './notesBoardColumnConfig';
import * as XLSX from 'xlsx';

const BoldRangePicker = styled(DatePicker.RangePicker)`
  .ant-picker-input > input {
    font-weight: 500;
  }
`;

const getAriaSortValue = sorted => {
  if (sorted === 'asc') return 'ascending';
  if (sorted === 'desc') return 'descending';
  return 'none';
};

const ITEMS_PER_PAGE = 50;

const NotesBoardPage = () => {
  const [loading, setLoading] = useState(false);
  const [theme] = useSelector(state => [state.themeReducer]);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [printOption, setPrintOption] = useState('audit');
  const routerLocation = useLocation();
  const params = new URLSearchParams(routerLocation.search);
  const tile = params.get('tile') || 'openJobs';
  const dashboardTotal = params.get('totalCount');
  const sideDrawerRef = useRef();
  const tableContainerRef = useRef(null);
  const topScrollRef = useRef(null);
  const history = useHistory();
  const user = useSelector(state => state.userReducer);

  // Session storage key scoped to the current tile
  const filterStorageKey = `notes_board_filters_${tile}`;

  // TanStack table state
  const [sortingState, setSortingState] = useState([
    { id: 'submissionTime', desc: true }
  ]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState([]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnSizing, setColumnSizing] = useState({});

  // Load persisted filters from session storage when tile changes
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(filterStorageKey);
      setColumnFilters(saved ? JSON.parse(saved) : []);
    } catch {
      setColumnFilters([]);
    }
  }, [filterStorageKey]);

  // Persist column filters to session storage whenever they change
  useEffect(() => {
    if (columnFilters.length > 0) {
      sessionStorage.setItem(filterStorageKey, JSON.stringify(columnFilters));
    } else {
      sessionStorage.removeItem(filterStorageKey);
    }
  }, [columnFilters, filterStorageKey]);

  // Column settings modal
  const [columnSettingsVisible, setColumnSettingsVisible] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_COLUMNS);

  const fetchColumnPreferences = useCallback(async () => {
    try {
      const response = await getColumnPreferences('DASHBOARD');
      if (
        response.data &&
        response.data.result &&
        Array.isArray(response.data.result) &&
        response.data.result.length > 0
      ) {
        const tileDefaults = {
          ...DEFAULT_COLUMNS,
          cptCodes: tile === 'eMRSubmittedUnbilledJobs'
        };
        const prefs = response.data.result.reduce((acc, item) => {
          if (item.columnName && item.columnName in DEFAULT_COLUMNS) {
            acc[item.columnName] = item.selected;
          }
          return acc;
        }, {});
        setVisibleColumns({ ...DEFAULT_COLUMNS, ...prefs, jobId: true });
      }
    } catch (error) {
      console.error('Failed to fetch column preferences:', error);
    }
  }, []);

  useEffect(() => {
    fetchColumnPreferences();
  }, [fetchColumnPreferences]);

  const handleSaveColumnSettings = newVisibleColumns => {
    setVisibleColumns(newVisibleColumns);
    const payload = Object.keys(newVisibleColumns).map(columnName => ({
      columnName,
      selected: newVisibleColumns[columnName]
    }));
    saveColumnPreferences('DASHBOARD', payload)
      .then(() => {
        notification.success({
          message: 'Success',
          description: 'Column preferences saved successfully'
        });
        fetchColumnPreferences();
      })
      .catch(() => {
        notification.error({
          message: 'Error',
          description: 'Failed to save column preferences. Please try again.'
        });
      });
  };

  // Sync visibleColumns to TanStack columnVisibility
  useEffect(() => {
    const visibility = Object.fromEntries(
      Object.entries(visibleColumns).map(([key, val]) => [key, val])
    );
    setColumnVisibility(visibility);
  }, [visibleColumns]);

  // Column ordering state for drag-and-drop
  const [columnOrder, setColumnOrder] = useState([]);
  const [draggedColumnId, setDraggedColumnId] = useState(null);
  const [dragOverColumnId, setDragOverColumnId] = useState(null);

  const tileName =
    tile === 'openJobs'
      ? 'Open Jobs'
      : tile === 'openJobsofLast14Days'
      ? 'Open Jobs of Last 14 Days'
      : tile === 'eMRSubmittedJobs'
      ? 'EMR Submitted Jobs'
      : tile === 'eMRSubmittedUnbilledJobs'
      ? 'EMR Submitted Unbilled Jobs'
      : tile === 'eMRSubmissionFailedJobs'
      ? 'EMR Submission Failed Jobs '
      : tile === 'eMRSubmissionFailedJobsofLast14Days'
      ? 'EMR Submission Failed Jobs of Last 14 Days'
      : tile === 'eMRSubmissionFailedJobsofLast1Month'
      ? 'EMR Submission Failed Jobs of Last 1 Month'
      : tile === 'allJobs'
      ? 'Jobs Created'
      : tile === 'openJobsOlderThan7Days'
      ? 'Open Jobs for more than 7 Days'
      : '';

  const status =
    tile === 'openJobs' || tile === 'openJobsofLast14Days'
      ? [
          'INPROGRESS_FOR_DOCTOR',
          'READY_FOR_REVIEWER',
          'INPROGRESS_FOR_REVIEWER'
        ]
      : tile === 'eMRSubmittedJobs'
      ? ['EMR_SUBMITTED']
      : tile === 'eMRSubmittedUnbilledJobs'
      ? ['EMR_SUBMITTED_NOT_BILLED']
      : tile === 'eMRSubmissionFailedJobs' ||
        tile === 'eMRSubmissionFailedJobsofLast14Days' ||
        tile === 'eMRSubmissionFailedJobsofLast1Month'
      ? ['EMR_SUBMISSION_FAILED']
      : tile === 'allJobs'
      ? [
          'INPROGRESS_FOR_DOCTOR',
          'READY_FOR_REVIEWER',
          'INPROGRESS_FOR_REVIEWER',
          'EMR_SUBMITTED',
          'EMR_SUBMITTED_BILLED',
          'EMR_SUBMITTED_NOT_BILLED',
          'EMR_SUBMISSION_FAILED',
          'STRUCK'
        ]
      : tile === 'openJobsOlderThan7Days'
      ? [
          'INPROGRESS_FOR_DOCTOR',
          'READY_FOR_REVIEWER',
          'INPROGRESS_FOR_REVIEWER'
        ]
      : [];

  // Tiles that default to a 1-month date range on initial load.
  const TILES_WITH_1_MONTH_DEFAULT = [
    'openJobs',
    'eMRSubmittedJobs',
    'eMRSubmittedUnbilledJobs',
    'eMRSubmissionFailedJobs',
    'eMRSubmissionFailedJobsofLast1Month',
    'allJobs'
  ];
  const isDatelessDefaultTile = false;

  // Date range state: older-than-7-days tile uses [null, -7d]; 1-month tiles use [-1mo, now]; others use [-14d, now]
  const [dateRange, setDateRange] = useState(
    tile === 'openJobsOlderThan7Days'
      ? [null, moment().subtract(7, 'days')]
      : TILES_WITH_1_MONTH_DEFAULT.includes(tile)
      ? [moment().subtract(1, 'month'), moment()]
      : [moment().subtract(14, 'days'), moment()]
  );
  // Separate display value for the picker: cleared when picker opens so user picks fresh
  const [pickerDisplayValue, setPickerDisplayValue] = useState(
    tile === 'openJobsOlderThan7Days'
      ? [null, moment().subtract(7, 'days')]
      : TILES_WITH_1_MONTH_DEFAULT.includes(tile)
      ? [moment().subtract(1, 'month'), moment()]
      : [moment().subtract(14, 'days'), moment()]
  );
  const [datePreset, setDatePreset] = useState(
    tile === 'openJobsOlderThan7Days'
      ? 'custom'
      : TILES_WITH_1_MONTH_DEFAULT.includes(tile)
      ? '1month'
      : '2weeks'
  );
  const [boardLoaded, setBoardLoaded] = useState(false);
  const createdFrom = dateRange[0] ? dateRange[0].valueOf() : '';
  const createdTill = dateRange[1] ? dateRange[1].valueOf() : '';

  let BreadCrubItems = [
    { name: 'Dashboard', path: '/dashboard' },
    { name: `${tileName}`, path: `/dashboard/${tile}` }
  ];

  // Fetch board config and set initial date range from board filter.
  // For tiles that should not have a default date, skip applying the board filter dates.
  useEffect(() => {
    getJobBoardsUser()
      .then(response => {
        if (response.data.success) {
          const boards = response.data.result[0].systemJobBoards;
          const board = boards.find(b => b.boardName === tileName);
          const filter = board?.boardFilter;

          if (!isDatelessDefaultTile) {
            if (filter?.relativeDaysForCreatedFrom != null) {
              const newRange = [
                moment().add(filter.relativeDaysForCreatedFrom, 'days'),
                moment()
              ];
              setDateRange(newRange);
              setPickerDisplayValue(newRange);
            } else if (filter?.relativeMonthsForCreatedFrom != null) {
              const newRange = [
                moment().add(filter.relativeMonthsForCreatedFrom, 'months'),
                moment()
              ];
              setDateRange(newRange);
              setPickerDisplayValue(newRange);
            } else if (filter?.relativeDaysForCreatedTill != null) {
              const newRange = [
                null,
                moment().add(filter.relativeDaysForCreatedTill, 'days')
              ];
              setDateRange(newRange);
              setPickerDisplayValue(newRange);
            }
          }
        }
      })
      .catch(error => {
        console.error('Error fetching board config:', error);
      })
      .finally(() => {
        setBoardLoaded(true);
      });
  }, [tileName]);

  useEffect(() => {
    if (boardLoaded) {
      fetchAll();
    }
  }, [dateRange, boardLoaded]);

  const handleDownloadCSV = () => {
    const EXPORT_STATUS_LABELS = {
      INPROGRESS_FOR_DOCTOR: 'In-Progress for Doctor',
      READY_FOR_REVIEWER: 'Ready for Reviewer',
      INPROGRESS_FOR_REVIEWER: 'In-Progress for Reviewer',
      EMR_SUBMITTED: 'EMR Submitted',
      EMR_SUBMITTED_BILLED: 'EMR Submitted Billed',
      EMR_SUBMITTED_NOT_BILLED: 'EMR Submitted Not Billed',
      DELETED: 'Deleted',
      EMR_SUBMISSION_FAILED: 'EMR Submission Failed',
      STRUCK: 'Struck'
    };
    const COLUMN_LABELS = {
      jobId: 'Job ID',
      author: 'Author',
      patientName: 'Patient Name',
      submissionTime: 'Submitted Time',
      effectiveDate: 'Encounter Date',
      facility: 'Facility',
      currentAssignee: 'Current Assignee',
      biller: 'Biller',
      workType: 'Work Type',
      dictationTemplate: 'Template',
      submissionToReviewerLevel1Time: 'Submission Time to Reviewer',
      rejectedByReviewerTime: 'Reviewer Rejected Time',
      billedTime: 'Billed Time',
      emrSubmissionTime: 'EMR Submission Time',
      cptCodes: 'CPT Codes',
      status: 'Status'
    };
    const DATE_COLUMNS = new Set([
      'submissionTime',
      'effectiveDate',
      'submissionToReviewerLevel1Time',
      'rejectedByReviewerTime',
      'billedTime',
      'emrSubmissionTime'
    ]);
    const formatDateExport = value => formatDateTime(value);
    const visibleCols = table.getVisibleLeafColumns();
    const filteredRows = table.getSortedRowModel().rows;

    const headers = visibleCols.map(col => COLUMN_LABELS[col.id] || col.id);
    const dataRows = filteredRows.map(row =>
      visibleCols.map(col => {
        const value = row.getValue(col.id);
        if (DATE_COLUMNS.has(col.id)) return formatDateExport(value);
        if (col.id === 'status')
          return EXPORT_STATUS_LABELS[value] || value || '';
        return value ?? '';
      })
    );

    const worksheetData = [headers, ...dataRows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Jobs');
    XLSX.writeFile(workbook, `${tile}.xlsx`);
  };

  const fetchAll = () => {
    setLoading(true);
    getJobBoardByStatusAndDate(status, createdFrom, createdTill)
      .then(response => {
        setLoading(false);
        if (response.data.success) {
          setTotal(response.data.size);
          setJobs(response.data.result);
        }
      })
      .catch(error => {
        setLoading(false);
        console.error('Error fetching job data:', error);
      });
  };

  const handlePresetChange = value => {
    setDatePreset(value);
    if (value === '2weeks') {
      const range = [moment().subtract(14, 'days'), moment()];
      setDateRange(range);
      setPickerDisplayValue(range);
    } else if (value === '1month') {
      const range = [moment().subtract(1, 'month'), moment()];
      setDateRange(range);
      setPickerDisplayValue(range);
    } else if (value === '3months') {
      const range = [moment().subtract(3, 'months'), moment()];
      setDateRange(range);
      setPickerDisplayValue(range);
    }
    // 'custom' — picker appears, user selects range manually
  };

  const handlePrint = useReactToPrint({
    content: () => {
      return sideDrawerRef.current;
    }
  });

  const viewJob = jobId => {
    history.push(
      `/dashboard/new-editor/${jobId}?jobStage=completed&userType=${user?.userType}`
    );
  };

  // TanStack column definitions
  const columns = useMemo(() => createNotesBoardColumns(), []);

  // Initialise column order whenever the column set changes
  useEffect(() => {
    setColumnOrder(columns.map(col => col.id));
  }, [columns]);

  // Column drag-and-drop handlers
  const handleColumnDragStart = useCallback((e, headerId) => {
    setDraggedColumnId(headerId);
    e.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleColumnDragOver = useCallback(
    (e, headerId) => {
      e.preventDefault();
      if (headerId !== draggedColumnId) {
        e.dataTransfer.dropEffect = 'move';
        setDragOverColumnId(headerId);
      }
    },
    [draggedColumnId]
  );

  const handleColumnDragLeave = useCallback(() => {
    setDragOverColumnId(null);
  }, []);

  const handleColumnDrop = useCallback(
    (e, targetId) => {
      e.preventDefault();
      if (!draggedColumnId || draggedColumnId === targetId) {
        setDraggedColumnId(null);
        setDragOverColumnId(null);
        return;
      }
      setColumnOrder(prev => {
        const newOrder = [...prev];
        const fromIdx = newOrder.indexOf(draggedColumnId);
        const toIdx = newOrder.indexOf(targetId);
        newOrder.splice(fromIdx, 1);
        newOrder.splice(toIdx, 0, draggedColumnId);
        return newOrder;
      });
      setDraggedColumnId(null);
      setDragOverColumnId(null);
    },
    [draggedColumnId]
  );

  const handleColumnDragEnd = useCallback(() => {
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  }, []);

  // TanStack table instance
  const table = useReactTable({
    data: jobs,
    columns,
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: ITEMS_PER_PAGE,
        pageIndex: 0
      }
    },
    getSortedRowModel: getSortedRowModel(),
    manualSorting: false,
    enableMultiSort: true,
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: 'includesString',
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    defaultColumn: { minSize: 0, maxSize: Number.MAX_SAFE_INTEGER },
    state: {
      sorting: sortingState,
      globalFilter,
      columnFilters,
      columnVisibility,
      columnSizing,
      columnOrder
    },
    onSortingChange: setSortingState,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onColumnOrderChange: setColumnOrder,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.jobId
  });

  const isEmpty = !jobs || jobs.length === 0;

  // Sync top scrollbar width with the table's scroll width
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    const updateWidth = () => {
      if (topScrollRef.current?.firstChild) {
        topScrollRef.current.firstChild.style.width = `${container.scrollWidth}px`;
      }
    };
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(container);
    return () => observer.disconnect();
  }, [jobs]);

  const handleTopScroll = () => {
    if (tableContainerRef.current && topScrollRef.current) {
      tableContainerRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
  };

  const handleBottomScroll = () => {
    if (topScrollRef.current && tableContainerRef.current) {
      topScrollRef.current.scrollLeft = tableContainerRef.current.scrollLeft;
    }
  };

  const StyledPrintSelect = styled.select`
    position: absolute;
    top: 20px;
    right: 100px;
    padding: 5px 12px;
    border: 1px solid #d9d9d9;
    background-color: white;
    color: #000;
    font-size: 14px;
    cursor: pointer;
    transition: border-color 0.3s ease, color 0.3s ease;

    &:hover {
      border-color: #1890ff;
      color: #1890ff;
    }

    &:focus {
      outline: none;
      border-color: #1890ff;
      box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
    }
  `;

  return (
    <ContainerRow gutter={[0, 40]}>
      {/* Title and Controls Section */}
      <TitleRow
        justify='space-between'
        align='middle'
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Title + Count */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CustomTypography
              color={theme['@text-color']}
              style={{ fontSize: 18, fontWeight: 600 }}
            >
              {tileName}
            </CustomTypography>
            <StyledJobListTabPaneContentCount className='tab-content-count'>
              <Badge
                count={
                  <span
                    style={{
                      fontSize: 22,
                      padding: '2px 6px',
                      color: 'white',
                      fontWeight: '600'
                    }}
                  >
                    {table.getFilteredRowModel().rows.length}
                    {dashboardTotal && `/${Number(dashboardTotal).toLocaleString()}`}
                  </span>
                }
                color={theme['@primary-color']}
                style={{ backgroundColor: theme['@primary-color'] }}
              />
            </StyledJobListTabPaneContentCount>
            {dateRange[0] && dateRange[1] && (
              <span
                style={{
                  fontSize: 13,
                  color: 'rgba(0, 0, 0, 0.85)',
                  fontWeight: 500
                }}
              >
                ({formatDate(dateRange[0])} - {formatDate(dateRange[1])})
              </span>
            )}
          </div>

          {/* Breadcrumb */}
          <BreadCrumbComponent items={BreadCrubItems} />
        </div>

        {/* Right side — Date Range + Search + Download */}
        <div
          style={{
            alignSelf: 'flex-end',
            display: 'flex',
            gap: '16px',
            alignItems: 'center'
          }}
        >
          <Select
            value={datePreset}
            onChange={handlePresetChange}
            style={{ width: 120 }}
            options={[
              { value: '2weeks',  label: <span style={{ fontWeight: 500 }}>2 Weeks</span> },
              { value: '1month',  label: <span style={{ fontWeight: 500 }}>1 Month</span> },
              { value: '3months', label: <span style={{ fontWeight: 500 }}>3 Months</span> },
              { value: 'custom',  label: <span style={{ fontWeight: 500 }}>Custom</span> }
            ]}
          />
          {datePreset === 'custom' && (
            <BoldRangePicker
              value={pickerDisplayValue}
              onOpenChange={open => {
                if (open) setPickerDisplayValue([null, null]);
              }}
              onChange={dates => {
                const newDates = dates || [null, null];
                setPickerDisplayValue(newDates);
                setDateRange(newDates);
              }}
              format='YYYY-MM-DD'
              allowClear={false}
              disabledDate={current => current && current > moment().endOf('day')}
              style={{ padding: '6px 12px' }}
            />
          )}
          <input
            type='text'
            placeholder='Search...'
            value={globalFilter ?? ''}
            onChange={e => setGlobalFilter(e.target.value)}
            style={{
              padding: '6px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              fontSize: 14,
              width: 220,
              outline: 'none'
            }}
          />
          <PrintButton
            icon={<DownloadOutlined />}
            onClick={handleDownloadCSV}
            style={{
              position: 'relative',
              top: 0,
              right: 'auto',
              fontWeight: '500'
            }}
          >
            Export to Excel
          </PrintButton>
          <SettingsButton
            icon={<SettingOutlined />}
            onClick={() => setColumnSettingsVisible(true)}
            style={{ marginLeft: 0 }}
          >
            Columns
          </SettingsButton>
          <SettingsButton
            onClick={() => {
              setColumnFilters([]);
              setGlobalFilter('');
            }}
            disabled={columnFilters.length === 0 && !globalFilter}
            style={{ marginLeft: 0 }}
          >
            Clear Filter
          </SettingsButton>
        </div>
      </TitleRow>

      <NotesBoardColumnSettingsModal
        visible={columnSettingsVisible}
        onCancel={() => setColumnSettingsVisible(false)}
        onSave={handleSaveColumnSettings}
        visibleColumns={visibleColumns}
      />

      {/* TanStack Table Section */}
      <ThemeProvider theme={theme}>
        <Styled.TableContainer
          ref={tableContainerRef}
          onScroll={handleBottomScroll}
          style={{ position: 'relative', width: '100%', minHeight: 350 }}
        >
          {/* Top scrollbar mirror — sticky inside the container so it stays at the column-header level */}
          <Styled.TopScrollbar ref={topScrollRef} onScroll={handleTopScroll}>
            <div style={{ height: 1 }} />
          </Styled.TopScrollbar>

          {/* Loading overlay */}
          {loading && (
            <Styled.LoadingOverlay>
              <Spinner />
            </Styled.LoadingOverlay>
          )}

          {/* Empty state */}
          {isEmpty && !loading ? (
            <Styled.EmptyState>
              <Empty description='No jobs found' />
            </Styled.EmptyState>
          ) : (
            <>
              {/* Table */}
              <Styled.StyledTable
                role='table'
                aria-label='Notes board table'
                style={{
                  width: table.getTotalSize(),
                  minWidth: '100%',
                  tableLayout: 'fixed',
                  transition: table.getState().columnSizingInfo.isResizingColumn
                    ? 'none'
                    : 'width 0.15s ease'
                }}
              >
                {/* Table Head */}
                <Styled.StyledTableHead role='rowgroup'>
                  {table.getHeaderGroups().map(headerGroup => (
                    <Styled.StyledTableHeaderRow
                      key={headerGroup.id}
                      role='row'
                    >
                      {headerGroup.headers.map(header => (
                        <Styled.DraggableHeader
                          key={header.id}
                          role='columnheader'
                          aria-sort={getAriaSortValue(
                            header.column.getIsSorted()
                          )}
                          style={{
                            width: header.getSize(),
                            transition: header.column.getIsResizing()
                              ? 'none'
                              : 'width 0.15s ease'
                          }}
                          sortable={header.column.getCanSort()}
                          sorted={header.column.getIsSorted()}
                          isDragging={draggedColumnId === header.id}
                          isOver={dragOverColumnId === header.id}
                          onDragOver={e => handleColumnDragOver(e, header.id)}
                          onDragLeave={handleColumnDragLeave}
                          onDrop={e => handleColumnDrop(e, header.id)}
                        >
                          <div
                            draggable
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              cursor: 'grab'
                            }}
                            onClick={
                              header.column.getCanSort() && !draggedColumnId
                                ? header.column.getToggleSortingHandler()
                                : undefined
                            }
                            tabIndex={header.column.getCanSort() ? 0 : -1}
                            onKeyDown={e => {
                              if (
                                header.column.getCanSort() &&
                                (e.key === 'Enter' || e.key === ' ')
                              ) {
                                header.column.getToggleSortingHandler()(e);
                              }
                            }}
                            onDragStart={e =>
                              handleColumnDragStart(e, header.id)
                            }
                            onDragEnd={handleColumnDragEnd}
                          >
                            {/* Sort indicator */}
                            {header.column.getCanSort() && (
                              <Styled.SortIndicator
                                direction={header.column.getIsSorted()}
                                style={{ marginLeft: 0 }}
                              >
                                <span className='sort-arrow-up'>&#9650;</span>
                                <span className='sort-arrow-down'>&#9660;</span>
                              </Styled.SortIndicator>
                            )}

                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </div>

                          {/* Resize handle */}
                          {header.column.getCanResize() && (
                            <Styled.ResizeHandle
                              onMouseDown={e => {
                                e.stopPropagation();
                                header.getResizeHandler()(e);
                              }}
                              onTouchStart={header.getResizeHandler()}
                              isResizing={header.column.getIsResizing()}
                              onClick={e => e.stopPropagation()}
                            />
                          )}
                        </Styled.DraggableHeader>
                      ))}
                    </Styled.StyledTableHeaderRow>
                  ))}
                </Styled.StyledTableHead>

                {/* Table Body */}
                <Styled.StyledTableBody role='rowgroup'>
                  {table.getRowModel().rows.map(row => (
                    <Styled.StyledTableRow
                      key={row.id}
                      role='row'
                      onClick={() => {
                        setSelectedJob(row.original);
                        setDrawerVisible(true);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      {row.getVisibleCells().map(cell => (
                        <Styled.StyledTableCell key={cell.id} role='cell'>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Styled.StyledTableCell>
                      ))}
                    </Styled.StyledTableRow>
                  ))}
                </Styled.StyledTableBody>
              </Styled.StyledTable>

              {/* Pagination Controls */}
              <Styled.PaginationContainer>
                <Styled.PaginationInfo>
                  Showing{' '}
                  {table.getState().pagination.pageIndex * ITEMS_PER_PAGE + 1}{' '}
                  to{' '}
                  {Math.min(
                    (table.getState().pagination.pageIndex + 1) *
                      ITEMS_PER_PAGE,
                    table.getFilteredRowModel().rows.length
                  )}{' '}
                  of {table.getFilteredRowModel().rows.length} results
                  {globalFilter &&
                    ` (filtered from ${jobs?.length || 0} total)`}
                </Styled.PaginationInfo>

                <Styled.PaginationControls>
                  <Tooltip title='First Page'>
                    <Styled.PaginationButton
                      type='text'
                      icon={<DoubleLeftOutlined />}
                      disabled={!table.getCanPreviousPage()}
                      onClick={() => table.setPageIndex(0)}
                      aria-label='First Page'
                    />
                  </Tooltip>

                  <Tooltip title='Previous Page'>
                    <Styled.PaginationButton
                      type='text'
                      icon={<LeftOutlined />}
                      disabled={!table.getCanPreviousPage()}
                      onClick={() => table.previousPage()}
                      aria-label='Previous Page'
                    />
                  </Tooltip>

                  <span>
                    Page {table.getState().pagination.pageIndex + 1} of{' '}
                    {table.getPageCount() || 1}
                  </span>

                  <Tooltip title='Next Page'>
                    <Styled.PaginationButton
                      type='text'
                      icon={<RightOutlined />}
                      disabled={!table.getCanNextPage()}
                      onClick={() => table.nextPage()}
                      aria-label='Next Page'
                    />
                  </Tooltip>

                  <Tooltip title='Last Page'>
                    <Styled.PaginationButton
                      type='text'
                      icon={<DoubleRightOutlined />}
                      disabled={!table.getCanNextPage()}
                      onClick={() =>
                        table.setPageIndex(table.getPageCount() - 1)
                      }
                      aria-label='Last Page'
                    />
                  </Tooltip>
                </Styled.PaginationControls>
              </Styled.PaginationContainer>
            </>
          )}
        </Styled.TableContainer>
      </ThemeProvider>

      {/* Side Drawer for Job Details */}
      <CustomSideDrawer
        title='Session Details'
        placement='right'
        closable={false}
        onClose={() => {
          setSelectedJob(null);
          setDrawerVisible(false);
        }}
        visible={drawerVisible}
        width={800}
        destroyOnClose={true}
      >
        <SessionDetails
          session={selectedJob}
          searchValue={''}
          selectedTab={1}
          ref={sideDrawerRef}
          printOption={printOption}
        />

        <div>
          <PrintButton icon={<PrinterOutlined />} onClick={handlePrint}>
            Print
          </PrintButton>

          <StyledPrintSelect
            value={printOption}
            onChange={e => setPrintOption(e.target.value)}
          >
            <option value='audit'>Audit</option>
            <option value='note'>Note</option>
            <option value='combined'>Note with Audit</option>
          </StyledPrintSelect>
        </div>

        {selectedJob &&
          ['Completed', 'Billed'].includes(selectedJob?.status) && (
            <StyledViewJobButton
              onClick={() => {
                viewJob(selectedJob?.jobId);
              }}
            >
              View
            </StyledViewJobButton>
          )}
      </CustomSideDrawer>

      {/* Print-only style */}
      <style>
        {`
        @media print {
          .ant-pagination {
            display: none !important;
          }
          button, .ant-btn {
            display: none !important;
          }
          .ant-breadcrumb {
            margin-bottom: 8px;
          }
        }
        `}
      </style>
    </ContainerRow>
  );
};

export default NotesBoardPage;
